/*
 * switch.js — progressive enhancement for the "Switch on More Than Glow" mechanic.
 *
 * The switch itself needs NO JavaScript: state is a checkbox (#GlowSwitch) and every
 * swap is CSS driven off body:has(#GlowSwitch:checked). See
 * snippets/switch-controller.liquid.
 *
 * This file adds only the three things CSS cannot do:
 *   1. <switch-bar>  reveal the sticky bar once the hero has scrolled past
 *   2. counters      animate stat numbers between their OFF and ON values
 *   2b. first use    stop the pill pulsing once the switch has been used
 *   2c. auto-switch  flip to ON when the reader scrolls past half the hero
 *   2d. auto-timer   flip to ON once, N ms after load, while still at the top
 *   3. a11y          make the bar's secondary toggle keyboard-operable
 *
 * If this file fails to load the page still works completely: the switch swaps, the
 * hero CTA still scrolls to the form, and the numbers show their final values. The
 * only loss is the sticky bar, which is a convenience.
 */

(function () {
  const SW_REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ 1. bar */

  if (!customElements.get('switch-bar')) {
    customElements.define(
      'switch-bar',
      class SwitchBar extends HTMLElement {
        connectedCallback() {
          /*
           * Observe the HERO, not a dedicated sentinel element.
           *
           * A zero-height marker div is the obvious implementation and it is a trap:
           * base.css hides every empty element (`div:empty { display: none }`), and a
           * display:none target is never laid out, so its boundingClientRect.top is
           * permanently 0 and the `top < 0` test below can never fire — the bar would
           * never appear. The hero always has real area, and "the hero has scrolled
           * off the top" is exactly the condition we want anyway.
           */
          // Matched by attribute, NOT by id: there are two hero variants (column and
        // banner) with different wrapper ids, and an id-based lookup silently fell
        // through to the fallback below on the banner one, showing the bar at the
        // top of the page. Any section that owns the switch marks itself with
        // data-sw-hero="{{ section.id }}".
        const hero = document.querySelector(`[data-sw-hero="${this.dataset.section}"]`);

          if (!('IntersectionObserver' in window) || !hero) {
            // Nothing to observe: show the bar rather than silently withhold the CTA.
            this.classList.add('is-visible');
            document.body.classList.add('sw-bar-active');
            return;
          }

          // IntersectionObserver rather than a scroll listener: the Instagram in-app
          // browser (80% of traffic) fires scroll erratically during momentum scroll.
          this.observer = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              // boundingClientRect.top < 0 means the hero went off the TOP, so the bar
              // does not appear when the hero is merely below the fold on load.
              const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle('is-visible', shouldShow);
              document.body.classList.toggle('sw-bar-active', shouldShow);
            },
            { threshold: 0 }
          );
          this.observer.observe(hero);
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          document.body.classList.remove('sw-bar-active');
        }
      }
    );
  }

  /* -------------------------------------------------------------- 2. counters */

  function swSetCounter(el, checked, animate) {
    // The authored strings are the source of truth, so "4.5" never becomes "4.50"
    // and a non-numeric value (an em dash, say) still renders correctly.
    const to = checked ? el.dataset.swCountOn : el.dataset.swCountOff;
    if (to == null) return;

    if (el.swRaf) cancelAnimationFrame(el.swRaf);

    const target = parseFloat(to);
    if (!animate || Number.isNaN(target)) {
      el.textContent = to;
      el.swRaf = null;
      return;
    }

    const from = parseFloat(el.textContent);
    const start = Number.isNaN(from) ? 0 : from;
    const decimals = (to.split('.')[1] || '').length;
    const duration = 900;
    const began = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - began) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = (start + (target - start) * eased).toFixed(decimals);

      if (progress < 1) {
        el.swRaf = requestAnimationFrame(tick);
      } else {
        el.textContent = to;
        el.swRaf = null;
      }
    };

    el.swRaf = requestAnimationFrame(tick);
  }

  function swInitCounters() {
    const input = document.getElementById('GlowSwitch');
    const nodes = document.querySelectorAll('[data-sw-count-on]');
    if (!input || !nodes.length) return;

    const canObserve = 'IntersectionObserver' in window;
    const inView = new Set();

    // A page can START switched on — the homepage takeover bar's own switch is checked in
    // the markup, and a browser can restore a checked box on Back. No `change` fires then,
    // so show the ON figures now; a stat that scrolls into view still replays its count.
    if (input.checked) nodes.forEach((node) => swSetCounter(node, true, false));

    // NOTE: reduced motion and a missing IntersectionObserver must still UPDATE the
    // number, just without animating it — otherwise the stat would keep showing the
    // OFF value while the rest of the page is switched ON.
    if (canObserve) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              inView.delete(entry.target);
              return;
            }
            inView.add(entry.target);
            // Replay from the OFF value each time it scrolls back in, so the count-up
            // is visible rather than a no-op animation between two identical numbers.
            if (input.checked && !SW_REDUCE_MOTION) {
              entry.target.textContent = entry.target.dataset.swCountOff;
              swSetCounter(entry.target, true, true);
            }
          });
        },
        { threshold: 0.4 }
      );

      nodes.forEach((node) => {
        if (node.dataset.swCountBound) return;
        node.dataset.swCountBound = 'true';
        observer.observe(node);
      });
    }

    // swInit re-runs on shopify:section:load, and #GlowSwitch survives a re-render
    // of any OTHER section — so bind its listener once or they stack up in the editor.
    if (input.swCountersBound) return;
    input.swCountersBound = true;

    input.addEventListener('change', () => {
      document.querySelectorAll('[data-sw-count-on]').forEach((node) => {
        swSetCounter(node, input.checked, inView.has(node) && !SW_REDUCE_MOTION);
      });
    });
  }

  /* ------------------------------------------------ 2c. auto-switch on scroll */

  function swInitAutoSwitch() {
    const hero = document.querySelector('[data-sw-autoswitch]');
    const input = document.getElementById('GlowSwitch');
    if (!hero || !input || hero.dataset.swAutoBound || !('IntersectionObserver' in window)) return;
    hero.dataset.swAutoBound = 'true';

    const fraction = parseFloat(hero.dataset.swAutoswitch) || 0.5;

    // Thresholds rather than a rootMargin: the trigger point is a fraction of the
    // HERO's height, which rootMargin (relative to the viewport) cannot express, and
    // the hero's height is not known until layout and changes on resize.
    const steps = [];
    for (let i = 0; i <= 20; i += 1) steps.push(i / 20);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const box = entry.boundingClientRect;
          // How far the hero has scrolled up past the top of the viewport.
          const scrolledPast = -box.top;
          if (scrolledPast < box.height * fraction) return;

          observer.disconnect();

          // Never fight the reader: if they have already touched the switch, their
          // choice stands and we do nothing.
          if (document.body.classList.contains('sw-used')) return;

          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          document.body.classList.add('sw-used');
        });
      },
      { threshold: steps }
    );

    observer.observe(hero);
  }

  /* ------------------------------------------------- 2d. auto-switch on a timer */

  /*
   * The homepage takeover's "switch-on moment": flip to ON once, a set time after the
   * page opens, so every visitor sees the transition while still at the top.
   *
   * It only ever fires on an untouched page whose hero is still near the top of the
   * viewport — so it can never swap content under someone mid-scroll — and only once
   * the ON image has actually loaded and decoded, so the cross-fade never reveals a
   * half-painted picture. If the image is not ready within delay + 3s it gives up and
   * the scroll trigger above remains the fallback.
   */
  function swInitAutoSwitchTimer() {
    const hero = document.querySelector('[data-sw-autoswitch-delay]');
    const input = document.getElementById('GlowSwitch');
    if (!hero || !input || hero.dataset.swTimerBound) return;
    hero.dataset.swTimerBound = 'true';

    // Never flip the switch while a merchant is editing the page.
    if (window.Shopify && window.Shopify.designMode) return;

    const delay = parseInt(hero.dataset.swAutoswitchDelay, 10);
    if (!(delay > 0)) return;

    // Any change — a tap on any switch, or the scroll trigger — cancels the timer.
    let done = false;
    input.addEventListener('change', () => { done = true; }, { once: true });

    const untouched = () =>
      !done && !input.checked && !document.body.classList.contains('sw-used');

    const nearTop = () => {
      const box = hero.getBoundingClientRect();
      return box.bottom > 0 && -box.top < box.height * 0.25;
    };

    // The <img> inside the ON state's <picture>; it holds whichever file the browser chose.
    const img = hero.querySelector('.sw-state--on img');
    const ready = new Promise((resolve) => {
      if (!img) return resolve(true);
      const decoded = () =>
        (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(true));
      if (img.complete) return img.naturalWidth > 0 ? decoded() : resolve(false);
      img.addEventListener('load', decoded, { once: true });
      img.addEventListener('error', () => resolve(false), { once: true });
    });
    const cap = new Promise((resolve) => setTimeout(() => resolve(false), delay + 3000));
    const wait = new Promise((resolve) => setTimeout(resolve, delay));

    Promise.all([wait, Promise.race([ready, cap])]).then(([, ok]) => {
      if (!ok) return;

      const fire = () => {
        if (!untouched() || !nearTop()) return;
        done = true;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        document.body.classList.add('sw-used');
      };

      if (document.visibilityState === 'visible') {
        fire();
        return;
      }

      // Opened in a background tab: wait until it is actually seen, then give the
      // visitor the same moment to take in the OFF state first.
      const onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVisible);
        setTimeout(fire, delay);
      };
      document.addEventListener('visibilitychange', onVisible);
    });
  }

  /* ------------------------------------------------- 2b. first-use affordance */

  function swInitFirstUse() {
    const input = document.getElementById('GlowSwitch');
    if (!input || input.swFirstUseBound) return;
    input.swFirstUseBound = true;

    // The whole page hinges on people noticing the switch, so the pill pulses until
    // it is first used. One class, set once, then the listener removes itself — the
    // pulse is an invitation, not a permanent animation.
    input.addEventListener(
      'change',
      () => document.body.classList.add('sw-used'),
      { once: true }
    );
  }

  /* ------------------------------------------------------------------ 3. a11y */

  function swInitSecondaryToggles() {
    const input = document.getElementById('GlowSwitch');
    if (!input) return;

    const toggles = document.querySelectorAll('[data-sw-secondary]');
    if (!toggles.length) return;

    // A <label for> is clickable but not focusable, so the bar's switch is
    // pointer-only until this runs. The hero switch wraps the real input and is
    // keyboard-operable with no JS at all, so keyboard users are never stranded.
    toggles.forEach((el) => {
      el.setAttribute('role', 'switch');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-checked', String(input.checked));

      if (el.dataset.swToggleBound) return;
      el.dataset.swToggleBound = 'true';

      /*
       * Drive the checkbox directly instead of letting the <label for> do it.
       *
       * Activating a label MOVES FOCUS to its control, and #GlowSwitch lives up in
       * the hero. WebKit (iOS Safari and the Instagram in-app browser — 80% of our
       * traffic) scrolls an off-screen focused control into view, so tapping the
       * sticky bar's switch threw the reader back to the top of the page.
       *
       * preventDefault() stops that activation; we flip the checkbox ourselves and
       * focus stays on the bar where the user actually tapped. `input.focus()` is
       * deliberately NOT called here for the same reason.
       *
       * Note this cannot be fixed by giving the input `position: fixed`: it sits
       * inside .switch-hero__content, which carries .scroll-trigger/animate--slide-in
       * and therefore a `transform`, making it the containing block for any fixed
       * descendant. The bar already depends on this script to appear at all, so
       * driving its toggle from here costs no extra degradation.
       */
      el.addEventListener('click', (event) => {
        event.preventDefault();
        input.checked = !input.checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      el.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        input.checked = !input.checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    if (input.swTogglesBound) return;
    input.swTogglesBound = true;

    input.addEventListener('change', () => {
      document
        .querySelectorAll('[data-sw-secondary]')
        .forEach((el) => el.setAttribute('aria-checked', String(input.checked)));
    });
  }

  /* -------------------------------------------------------------------- init */

  function swInit() {
    swInitCounters();
    swInitSecondaryToggles();
    swInitFirstUse();
    swInitAutoSwitch();
    swInitAutoSwitchTimer();
  }

  swInit();

  // Re-wire after a section re-render in the theme editor.
  document.addEventListener('shopify:section:load', swInit);
})();
